/**
 * Spec 038 sections 4.1, 4.3, 5: pure mapping from the chart model to library
 * data, plus the range and margin maths. No DOM.
 *
 * Range convention (measured, findings P0b): a logical range's `from`/`to`
 * are the indexes of the first and last visible bar CENTRES, so bars
 * `start..end` fill the plot as `{from: start, to: end}`; empty slots on the
 * right are `to = end + k`. The library applies `setVisibleLogicalRange` on
 * the next paint, so code must never read a range back in the tick it set it.
 */
import type { CandlestickData, LineData, UTCTimestamp } from 'lightweight-charts'
import type { DrawCandle, DrawEquityPoint, DrawMarker } from '@/ui/lib/quantChart/types'

/** First index with `times[i] >= t` (`times.length` when none). */
export function lowerBound(times: number[], t: number): number {
  let lo = 0
  let hi = times.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (times[mid] < t) lo = mid + 1
    else hi = mid
  }
  return lo
}

/** Last index with `times[i] <= t` (-1 when none). */
export function upperIndex(times: number[], t: number): number {
  // First index with `times[i] > t`, minus one. (`lowerBound(t + epsilon)` is
  // wrong for epoch-sized times: 1.7e9 + 1e-9 is 1.7e9 in a double, so a time
  // that equals a bar's time would exclude that bar.)
  let lo = 0
  let hi = times.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (times[mid] <= t) lo = mid + 1
    else hi = mid
  }
  return lo - 1
}

const utc = (t: number): UTCTimestamp => t as UTCTimestamp

/**
 * Candles to library data. A doji (`close === open`) needs no override: the
 * library draws it as "up", matching the old `close >= open` rule (spike).
 */
export function candlesToData(candles: DrawCandle[]): CandlestickData<UTCTimestamp>[] {
  return candles.map((c) => ({ time: utc(c.time), open: c.open, high: c.high, low: c.low, close: c.close }))
}

/** One overlay line; `null` values are omitted so the line connects across warm-up and gaps. */
export function linesToData(values: Array<number | null>, times: number[]): LineData<UTCTimestamp>[] {
  const out: LineData<UTCTimestamp>[] = []
  const n = Math.min(values.length, times.length)
  for (let i = 0; i < n; i += 1) {
    const v = values[i]
    if (v === null || v === undefined) continue
    out.push({ time: utc(times[i]), value: v })
  }
  return out
}

/** How to bring an overlay line series up to date after the candles gained or changed their tail. */
export type LineWritePlan = { kind: 'tail'; points: LineData<UTCTimestamp>[] } | { kind: 'full' }

/**
 * Spec 038 P23: a candle tick or an appended bar used to re-send every overlay
 * line in full (20 lines x 10,000 points per tick). When the values before
 * index `from` are exactly what the series already holds and the tail has no
 * hole where a point used to be, only the points from `from` on need an
 * `update`; otherwise the safe answer is the full `setData`.
 */
export function planLineWrite(prev: Array<number | null>, next: Array<number | null>, times: number[], from: number): LineWritePlan {
  if (from < 0 || prev.length < from || next.length < from) return { kind: 'full' }
  for (let i = 0; i < from; i += 1) {
    if (prev[i] !== next[i]) return { kind: 'full' }
  }
  const points: LineData<UTCTimestamp>[] = []
  const n = Math.min(next.length, times.length)
  for (let i = from; i < n; i += 1) {
    const v = next[i]
    if (v === null || v === undefined) {
      if (i < prev.length && prev[i] !== null && prev[i] !== undefined) return { kind: 'full' }
      continue
    }
    points.push({ time: utc(times[i]), value: v })
  }
  return { kind: 'tail', points }
}

/** Theme-token name for a marker's colour: explicit colour, else by kind. */
export function markerColorToken(m: Pick<DrawMarker, 'kind' | 'color'>): string {
  if (m.color) return m.color
  if (m.kind === 'entry') return 'paper'
  if (m.kind === 'skip' || m.kind === 'roll') return 'dim'
  if (m.kind === 'candidate_long' || m.kind === 'candidate_short') return 'paper'
  return 'accent'
}

/** A marker snapped onto the candle grid, ready for the overlay primitive (driver decision 5). */
export interface SnappedMarker {
  /** Index of the first candle at or after the marker time. */
  index: number
  time: number
  above: boolean
  kind: string
  /** `"<text> <price>"` exactly as the old renderer prints it. */
  label: string
  /** Colour token (`paper`, `dim`, `accent`) or a literal data colour; resolve with `resolveDataColor` (`dim` is `QuantChartColors.dim`). */
  color: string
  /** Triangle half-size in px: 6 for an entry, 4.5 otherwise. */
  half: number
}

/**
 * A marker's label: its text plus the price, except when the text is itself a
 * number (real strategies send the price as the text), which is shown once.
 */
export function markerLabel(text: string, price: number | undefined, digits: number): string {
  if (price === undefined) return text
  const trimmed = text.trim()
  if (trimmed !== '' && Number.isFinite(Number(trimmed))) return trimmed
  const p = price.toFixed(Math.max(0, digits))
  return trimmed === '' ? p : `${text} ${p}`
}

/** Snaps markers forward to the first candle at or after their time; markers after the last candle are dropped. */
export function markersToOverlay(markers: DrawMarker[], times: number[], digits: number): SnappedMarker[] {
  const out: SnappedMarker[] = []
  for (const m of markers) {
    const index = lowerBound(times, m.time)
    if (index >= times.length) continue
    out.push({
      index,
      time: times[index],
      above: m.above,
      kind: m.kind,
      label: markerLabel(m.text, m.price, digits),
      color: markerColorToken(m),
      half: m.kind === 'entry' ? 6 : 4.5,
    })
  }
  return out
}

/**
 * Equity points snapped to the candle grid so they never add time-scale
 * slots: each point moves to the first candle at or after its time, the last
 * value per candle time wins, and points after the last candle are dropped.
 */
export function equityToData(equity: DrawEquityPoint[], times: number[]): LineData<UTCTimestamp>[] {
  return snapEquityToCandles(equity, times).map((p) => ({ time: utc(p.time), value: p.value }))
}

/**
 * Spec 038 20.6 rule 2 (pure, library-free): moves each equity point to the
 * first candle at or after its time (a point before the first candle lands on
 * the first candle), keeps the last value per candle time (input is first
 * stable-sorted by time, so unsorted input and duplicates are fine), drops
 * points after the last candle and non-finite points. Every returned time is
 * a candle time, ascending, so the time scale gains no slots.
 */
export function snapEquityToCandles(equity: readonly DrawEquityPoint[], times: number[]): DrawEquityPoint[] {
  if (times.length === 0) return []
  const sorted = equity
    .filter((p) => Number.isFinite(p.time) && Number.isFinite(p.value))
    .map((p, order) => ({ p, order }))
    .sort((a, b) => a.p.time - b.p.time || a.order - b.order)
  const byIndex = new Map<number, number>()
  for (const { p } of sorted) {
    const i = lowerBound(times, p.time)
    if (i >= times.length) continue
    byIndex.set(i, p.value)
  }
  return [...byIndex.entries()].sort((a, b) => a[0] - b[0]).map(([i, value]) => ({ time: times[i], value }))
}

/**
 * Spec 038 20.6 rule 4: a stored curve for the equity-only chart, which has no
 * candle grid. The library needs strictly ascending, unique times: sort by
 * time (stable), keep the last value of duplicates, drop non-finite points.
 */
export function equityCurveToData(points: ReadonlyArray<readonly [number, number]>): LineData<UTCTimestamp>[] {
  const sorted = points
    .filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .map((p, order) => ({ p, order }))
    .sort((a, b) => a.p[0] - b.p[0] || a.order - b.order)
  const out: LineData<UTCTimestamp>[] = []
  for (const { p } of sorted) {
    const last = out[out.length - 1]
    if (last !== undefined && last.time === p[0]) last.value = p[1]
    else out.push({ time: utc(p[0]), value: p[1] })
  }
  return out
}

export interface LogicalSpan {
  from: number
  to: number
}

/**
 * Bars `start..end` (inclusive indexes) as a logical range. `slots` is the
 * fixed slot count of a truncated replay: when larger than the bar count the
 * extra slots stay empty on the right.
 */
export function windowToRange(start: number, end: number, slots?: number): LogicalSpan {
  const bars = end - start + 1
  const n = slots !== undefined && slots > bars ? slots : bars
  return { from: start, to: start + n - 1 }
}

export interface WindowSpan {
  start: number
  end: number
  /** Visible bar count (empty right-hand slots excluded). */
  span: number
  /** Bars scrolled back from the newest bar (0 = at the latest). */
  offset: number
}

/** Inverse of `windowToRange` for `count` bars; rounds the centres and clamps to the data. */
export function rangeToWindow(range: LogicalSpan, count: number): WindowSpan {
  const last = Math.max(0, count - 1)
  const start = Math.min(last, Math.max(0, Math.round(range.from)))
  const end = Math.min(last, Math.max(start, Math.round(range.to)))
  return { start, end, span: end - start + 1, offset: last - end }
}

/**
 * Scale margins that reproduce the old price padding (10% of the candle range
 * plus an 8 px inset) for a main pane `paneHeightPx` tall:
 * `m = 1/12 + 20/(3H)` (measured, findings P0b). Recompute on resize.
 */
export function scaleMargins(paneHeightPx: number): { top: number; bottom: number } {
  if (!(paneHeightPx > 0)) return { top: 0.1, bottom: 0.1 }
  const m = Math.min(0.4, 1 / 12 + 20 / (3 * paneHeightPx))
  return { top: m, bottom: m }
}

export type CandleChange = 'none' | 'update-last' | 'append' | 'replace'

/**
 * Chooses between `update` and `setData` for a new candle array:
 * - `none`: nothing changed;
 * - `update-last`: same length, same last time, different last values;
 * - `append`: strictly later bars were added after an unchanged-time last bar
 *   (apply `update` to `next.slice(prev.length - 1)`: the first one refreshes
 *   the old last bar, the rest are new);
 * - `replace`: anything else (history changed, shrank or shifted): `setData`.
 * Only the first, last and last-previous times are compared, not every bar.
 */
export function classifyCandleChange(prev: DrawCandle[], next: DrawCandle[]): CandleChange {
  if (prev.length === 0 && next.length === 0) return 'none'
  if (prev.length === 0 || next.length === 0 || next.length < prev.length) return 'replace'
  if (next[0].time !== prev[0].time) return 'replace'
  const pl = prev.length - 1
  if (next[pl].time !== prev[pl].time) return 'replace'
  if (next.length === prev.length) {
    const a = prev[pl]
    const b = next[pl]
    return a.open === b.open && a.high === b.high && a.low === b.low && a.close === b.close ? 'none' : 'update-last'
  }
  for (let i = pl + 1; i < next.length; i += 1) {
    if (next[i].time <= next[i - 1].time) return 'replace'
  }
  return 'append'
}

/**
 * Spec 076: how many bars a sliding window dropped from the front. A full window that receives a new bar
 * keeps its length, so `classifyCandleChange` says `replace`; the old bars are still there, shifted left.
 * Returns that shift (0 when `next` is not the previous array moved by whole bars).
 */
export function slideCount(prev: DrawCandle[], next: DrawCandle[]): number {
  if (prev.length === 0 || next.length === 0) return 0
  const k = prev.findIndex((c) => c.time === next[0].time)
  if (k <= 0) return 0
  const lastPrev = next[prev.length - 1 - k]
  return lastPrev !== undefined && lastPrev.time === prev[prev.length - 1].time ? k : 0
}
